import { useMemo } from "react";
import { EntityTypes } from "@vzbl/integrations";
import { useApplicationContext } from "src/hooks/useApplication";
import { JiraIcon } from "src/components/JiraIcon";
import { useEntityContext } from "../useEntityContext";
import { TabbedWork } from "../components/TabbedWork";
import { VzblTag } from "../components/VzblTag";
import { generateWorkTabs } from "../components/worktabs";
import { Work, WorkCommentsComponent, WorkComponent } from "../Work";
import { TimeMachine, TimeMachineComponent } from "../TimeMachine";
import { Insights } from "../Insights";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { SummaryTag, MetricTag, RiskTag } from "./components/Tags";
import { SentencesList } from "./components/SentencesList";
import { ProgressMetric } from "./components/ProgressMetric";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { toHeaderAttributes } from "./components/HeaderAttributes";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { EntityTabs } from "./components/EntityTabs";
import { Urgency } from "./components/Urgency";
import { Impact } from "./components/Impact";
import { Tooltip } from "antd";
import styles from "./components/integration-markdown.module.css";
import { processJiraMarkdown } from "src/utils/jira-markdown";
import { ReportsLinks } from "../components/Reports";
import { BetaTag } from "src/components/BetaTag";

export const IssueEntity = () => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entity = context.dataRef.current.entity;
  const properties = entity.properties;
  const entityType = context.dataRef.current.entity.labels[0];

  const {
    description,
    integration,
    issueKey,
    issueType,
    priority,
    status,
    title,
    uuid,
    vzblClarityExplanation,
    vzblClarityScore,
    vzblClassification,
    vzblClassificationExplanation,
    vzblFrictionExplanation,
    vzblFrictionScore,
    vzblImpact,
    vzblImpactExplanation,
    vzblMissingDetails,
    vzblUnansweredQuestions,
    vzblUrgency,
    vzblUrgencyExplanation,
    vzblValueStatement,
  } = properties;

  const markdown = useMemo(
    () => processJiraMarkdown(description, integration),
    [description, integration]
  );

  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          entity={context.dataRef.current.entity}
          attributes={toHeaderAttributes({
            orgId,
            context,
            entityType,
            attributes: [
              { name: "issueType", text: issueType },
              { name: "priority", text: priority },
              { name: "status", text: status },
            ],
          })}
          icon={<JiraIcon size={40} />}
          label={issueKey}
        />
      }
      summaryPanel={
        <SummaryPanel
          title={title}
          description={markdown}
          className={styles.content}
        />
      }
      insightsPanel={
        <InsightsPanel
          cards={[
            vzblUrgency !== undefined && (
              <ActionCard
                key="urgency"
                badge={<MetricTag />}
                label="Urgency"
                value={
                  <VzblTag
                    orgId={orgId}
                    context={context}
                    text={vzblUrgency}
                    color="none"
                    key="urgency"
                    linkParams={{
                      orgId,
                      entityType,
                      attribute: "vzblUrgency",
                      value: vzblUrgency,
                    }}
                  />
                }
              >
                <Urgency
                  value={vzblUrgency}
                  explanation={vzblUrgencyExplanation}
                />
              </ActionCard>
            ),
            vzblImpact !== undefined && (
              <ActionCard
                key="impact"
                badge={<MetricTag />}
                label="Impact"
                value={
                  <VzblTag
                    orgId={orgId}
                    context={context}
                    text={vzblImpact}
                    color="none"
                    key="impact"
                    linkParams={{
                      orgId,
                      entityType,
                      attribute: "vzblUrgency",
                      value: vzblImpact,
                    }}
                  />
                }
              >
                <Impact
                  value={vzblImpact}
                  explanation={vzblImpactExplanation}
                />
              </ActionCard>
            ),
            vzblClarityScore !== undefined && (
              <ActionCard
                key="clarity"
                badge={<MetricTag />}
                label="Clarity"
                value={
                  <ProgressMetric
                    higherBetter
                    value={vzblClarityScore}
                    data={vzblClarityExplanation}
                  />
                }
              />
            ),
            vzblFrictionScore !== undefined && (
              <ActionCard
                key="friction"
                badge={<MetricTag />}
                label="Friction"
                value={
                  <ProgressMetric
                    lowerBetter
                    value={vzblFrictionScore}
                    data={vzblFrictionExplanation}
                  />
                }
              />
            ),
            vzblValueStatement && (
              <ActionCard
                key="value-statement"
                badge={<SummaryTag />}
                label="Value Statement"
                value={<SentencesList showNa data={vzblValueStatement} />}
              />
            ),
            (vzblClassification?.length || 0) > 0 &&
              (vzblClassificationExplanation?.length || 0) > 0 && (
                <ActionCard
                  key="classification"
                  badge={<SummaryTag />}
                  label={
                    <Tooltip
                      placement="left"
                      title={
                        <SentencesList
                          showNa
                          data={vzblClassificationExplanation}
                        />
                      }
                    >
                      Classification
                    </Tooltip>
                  }
                  value={
                    <VzblTag
                      orgId={orgId}
                      context={context}
                      text={vzblClassification}
                      color="none"
                      key="classification"
                      linkParams={{
                        orgId,
                        entityType: entityType,
                        attribute: "vzblClassification",
                        value: vzblClassification,
                      }}
                    />
                  }
                ></ActionCard>
              ),
            vzblMissingDetails !== undefined &&
              vzblMissingDetails.length > 0 && (
                <ActionCard
                  key="missing-details"
                  badge={<RiskTag />}
                  label="Missing Details"
                  value={<SentencesList showNa data={vzblMissingDetails} />}
                />
              ),
            vzblUnansweredQuestions !== undefined &&
              vzblUnansweredQuestions.length > 0 && (
                <ActionCard
                  key="unanswered-questions"
                  badge={<RiskTag />}
                  label="Questions Asked"
                  value={
                    <SentencesList showNa data={vzblUnansweredQuestions} />
                  }
                />
              ),
          ].filter(Boolean)}
        />
      }
    >
      <EntityTabs
        propertiesSkipList={[
          "issueType",
          "priority",
          "status",
          "title",
          "name",
          "summary",
          "description",
          "vzblClassification",
          "vzblClassificationExplanation",
          "vzblValueStatement",
          "vzblMissingDetails",
          "vzblUnansweredQuestions",
          "vzblClarityScore",
          "vzblClarityExplanation",
          "vzblFrictionScore",
          "vzblFrictionExplanation",
          "vzblImpact",
          "vzblImpactExplanation",
          "vzblUrgency",
          "vzblUrgencyExplanation",
        ]}
        entity={context.dataRef.current.entity}
        tabs={[
          {
            key: "overview",
            id: "overview",
            label: "Overview",
            children: <Insights entity={context.dataRef.current.entity} />,
          },
          {
            key: "reports",
            id: "reports",
            label: (
              <>
                Reports <BetaTag size="small" />
              </>
            ),
            children: (
              <ReportsLinks
                filterable={EntityTypes.Issue}
                filter={issueKey}
                defaultTimeframe={{
                  duration: "P30D",
                }}
              />
            ),
          },
          {
            key: "2",
            id: "2",
            label: "Work",
            children: (
              <>
                <TabbedWork
                  items={generateWorkTabs(orgId, properties.uuid, [
                    { entityTypes: EntityTypes.Commit },
                    {
                      entityTypes: EntityTypes.PullRequest,
                      label: "Pull Requests",
                    },
                    {
                      entityTypes: EntityTypes.CicdPipeline,
                      label: "Deployments",
                    },
                    { entityTypes: EntityTypes.Worklog },
                  ])}
                />
                <br />
                <Work orgId={orgId} entityUuid={uuid} types={["Issue"]}>
                  <WorkComponent title="Related Issues" />
                </Work>
              </>
            ),
          },
          {
            key: "3",
            id: "3",
            label: "Collaboration",
            children: (
              <>
                <Work
                  orgId={orgId}
                  entityUuid={uuid}
                  types={[EntityTypes.IssueComment]}
                >
                  <WorkCommentsComponent title="Comments" />
                </Work>
                <br />
                <TabbedWork
                  title="Communications"
                  items={generateWorkTabs(orgId, properties.uuid, [
                    {
                      entityTypes: EntityTypes.CalendarEntry,
                      label: "Meetings",
                    },
                    { entityTypes: EntityTypes.Message },
                  ])}
                />
              </>
            ),
          },
          {
            key: "5",
            id: "5",
            label: "History",
            children: (
              <TimeMachine>
                <TimeMachineComponent />
              </TimeMachine>
            ),
          },
        ]}
      />
    </EntityScreen>
  );
};
